import { useContext, useEffect } from 'react'
import { useShallow } from 'zustand/react/shallow'

import { PrintingsFinalExaminationsPageUI } from './ui'
import { IPrintingsFinalExaminations } from './types'

import { usePrintingsDiplomas } from '@api/printingsDiplomas/state'

import { useValue } from '@hooks/useValue'
import { useDebounce } from '@hooks/useDebounce'

import { IModalConfirm, ModalConfirm } from '@primitive/modal.confirm'

import { CardContractContainer, ICardContractContainer } from '@smart/cardContract'
import { CardListenerContainer, ICardListenerContainer } from '@smart/cardListener'
import { NotificationContext, notifyOpen } from '@ui/notification/notification.provider'

import { CardPrintingDiplomasContainer, ICardPrintingDiplomasContainer } from '../../components/smart/cardPrintingDiplomas'


/** Контейнер - Страница работ на печать */
export const PrintingsFinalExaminationsContainer = () => {
    const notify = useContext(NotificationContext)

    const {
        error,
        isLoading,
        isReloadListeners,
        printingsList,
        pageSize,
        totalCount,
        getPrintingDiplomasListeners,
        deletePrintDiplomas,
        clearPrintingDiplomasState,
    } = usePrintingsDiplomas(useShallow((state) => ({
        error: state.error,
        isLoading: state.listeners.isLoading,
        isReloadListeners: state.isReloadListeners,
        printingsList: state.listeners.items,
        pageSize: state.listeners.pageSize,
        totalCount: state.listeners.totalCount,
        getPrintingDiplomasListeners: state.actionGetPrintingDiplomasListeners,
        deletePrintDiplomas: state.actionSetPrintingDiplomasDelete,
        clearPrintingDiplomasState: state.clearPrintingDiplomasState,
    })))

    const cardContract = useValue<ICardContractContainer>({isOpen: false})
    const cardListener = useValue<ICardListenerContainer>({isOpen: false})
    const cardPrinting = useValue<ICardPrintingDiplomasContainer>({isOpen: false})

    const loadingTitle = useValue<string>('Загрузка документов...')
    const modalConfirm = useValue<IModalConfirm>({isOpen: false})

    const currentPageNumber = useValue<number>(1)
    const txtSearch = useValue<string>('')

    useEffect(() => {
        return () => {
            clearPrintingDiplomasState()

            currentPageNumber.setValue(1)
            txtSearch.setValue('')
        }
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        loadDataListeners(txtSearch.value)
    }, [currentPageNumber.value]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (error) return notify && notifyOpen(error.message, error.type, 2000, notify)
    }, [error]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        isReloadListeners && loadDataListeners(txtSearch.value)
    }, [isReloadListeners]) // eslint-disable-line react-hooks/exhaustive-deps

    const loadDataListeners = (search?: string) => {
        getPrintingDiplomasListeners(currentPageNumber.value, search || '')
    }

    const handlerOpenModalConfirm = (isOpen: boolean, headerText: string, contentText: string, actionName?: string, recordID?: number) => {
        modalConfirm.setValue({
            isOpen, headerText, contentText,
            handleButtonCancel() { modalConfirm.setValue({isOpen: false}) },
            handleButtonOk() {
                loadingTitle.setValue('Возвращаю в раздел итоговой аттестации...')

                deletePrintDiplomas(Number(recordID)).then((res) => {
                    if (res === 200) {
                        loadDataListeners(txtSearch.value)
                        return notify && notifyOpen('Анкета итоговой работы слушателя успешно возвращена на обработку.', 'success', 1500, notify)
                    }
                }).finally(() => {
                    loadingTitle.setValue('Загрузка документов...')
                })

                modalConfirm.setValue({isOpen: false})
            }
        })
    }

    const handleOpenCardContract = (isOpen: boolean, contractID?: number) => {
        cardContract.setValue({
            isOpen, cardMode: 'edit', cardID: contractID,
            handlerButtonCancel() { cardContract.setValue({isOpen: false}) },
            handlerButtonOk() {
                cardContract.setValue({isOpen: false})
                loadDataListeners(txtSearch.value)
            }
        })
    }

    const handleOpenCardListener = (isOpen: boolean, listenerID?: number) => {
        cardListener.setValue({
            isOpen, cardMode: 'edit', cardID: listenerID,
            handlerButtonCancel() { cardListener.setValue({isOpen: false}) },
            handlerButtonOk() {
                cardListener.setValue({isOpen: false})
                loadDataListeners(txtSearch.value)
            }
        })
    }

    const handleOpenCardPrinting = (isOpen: boolean, printID?: number, fullNameFrom?: string, dataBase?: string) => {
        cardPrinting.setValue({
            isOpen, cardID: printID, fullNameFrom, dataBase,
            handlerButtonCancel() { cardPrinting.setValue({isOpen: false}) },
            handlerButtonOk() {
                cardPrinting.setValue({isOpen: false})
                loadDataListeners(txtSearch.value)
            }
        })
    }

    const debounceSearch = useDebounce(loadDataListeners, 1500)
    const txtSearchOnChange = (value: string) => { txtSearch.setValue(value); debounceSearch(value) }

    const propsToComponent: IPrintingsFinalExaminations = {
        isLoading,
        loadingTitle,
        printingsList,
        txtSearch,
        currentPageNumber,
        totalCount,
        pageSize,
        txtSearchOnChange,
        loadDataListeners,
        handleOpenCardContract,
        handleOpenCardListener,
        handleOpenCardPrinting: (isOpen: boolean, printID: number, fullNameFrom: string, dataBase: string) => handleOpenCardPrinting(isOpen, printID, fullNameFrom, dataBase),
        handlerOpenModalConfirm,
    }

    return <>
        <PrintingsFinalExaminationsPageUI {...propsToComponent} />

        <ModalConfirm {...modalConfirm.value} />
        <CardListenerContainer {...cardListener.value} />
        <CardContractContainer {...cardContract.value} />
        <CardPrintingDiplomasContainer {...cardPrinting.value} />
    </>
}